import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/config'
import Constants from '../../utils/constants';

export const createTransaction = createAsyncThunk(
  'transaction/create',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/v1/admin/transactions/new', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create transaction');
    }
  }
);

export const getAllTransactions = createAsyncThunk(
  'transaction/getAll',
  async ({ page = 1, perPage = 10, ...filters }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, perPage });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axiosInstance.get(`/api/v1/transactions?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const updateTransaction = createAsyncThunk(
  'transaction/update',
  async ({ id, transactionData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/v1/admin/transactions/${id}`, transactionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update transaction');
    }
  }
);

export const bulkCreateTransactions = createAsyncThunk(
  'transaction/bulkCreate',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post('/api/v1/admin/transactions/bulk-create', formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk create transactions');
    }
  }
);

export const uploadInvoice = createAsyncThunk(
  'transaction/uploadInvoice',
  async ({ transactionId, file, name }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('transactionId', transactionId);
      formData.append('category', Constants.DOCUMENT_CATEGORIES.INVOICE)
      if (name) formData.append('name', name);

      const response = await axiosInstance.post('/api/v1/admin/transactions/upload-invoice', formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload invoice');
    }
  }
);

const transactionSlice = createSlice({
  name: 'transaction',
  initialState: {
    transactions: [],
    pagination: {},
    loading: false,
    error: null,
    isCreated: false,
    isUpdated: false,
    isBulkCreated: false,
    isInvoiceUploaded: false,
    bulkResult: null
  },
  reducers: {
    clearFlags: (state) => {
      state.isCreated = false;
      state.isUpdated = false;
      state.isBulkCreated = false;
      state.isInvoiceUploaded = false;
      state.error = null;
      state.bulkResult = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.isCreated = true;
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.isUpdated = true;
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(bulkCreateTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkCreateTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.isBulkCreated = true;
        state.bulkResult = action.payload.data;
      })
      .addCase(bulkCreateTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.isInvoiceUploaded = true;
      })
      .addCase(uploadInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearFlags } = transactionSlice.actions;
export default transactionSlice.reducer;